// i cant figure out how to import these from logrocket so imma just yoink em:
interface IRequest {
  reqId: string
  url: string
  headers: { [key: string]: string | undefined }
  body?: string
  method: string
  referrer?: string
  mode?: string
  credentials?: string
}

interface IResponse {
  reqId: string
  status?: number
  headers: { [key: string]: string | undefined }
  body?: string
  method: string
}

// https://docs.logrocket.com/reference/network

const REDACTED = '[redacted]'

/**
 * Processor credentials — Point of Banking merchant keys, Payomi POS keys, Aeropay merchant ids —
 * are posted as validateProcessorCredentials variables while an internal user types them. Keep the
 * operation itself visible for debugging but strip every credential out of it, and drop the whole
 * body if it does not parse into the shape we expect, so an unrecognised one is never recorded.
 */
const redactProcessorCredentials = (body: string): string | undefined => {
  let parsed: unknown
  try {
    parsed = JSON.parse(body)
  } catch {
    return undefined
  }

  const operations = Array.isArray(parsed) ? parsed : [parsed]

  for (const operation of operations) {
    const settings = (
      operation as {
        variables?: { settings?: Record<string, unknown> }
      }
    )?.variables?.settings

    if (!settings || typeof settings !== 'object') return undefined

    if ('key' in settings) settings.key = REDACTED

    const fields = settings.fields
    if (fields && typeof fields === 'object')
      for (const name of Object.keys(fields))
        (fields as Record<string, unknown>)[name] = REDACTED
  }

  return JSON.stringify(parsed)
}

/**
 * The Internal Tools client form posts the write-only Okta client secret as
 * createClient/updateClient variables (settings.ssoClientSecret, nested under client for
 * updates). Redact just the secret so the rest of the operation stays debuggable, and drop
 * the whole body when it does not parse into the expected shape.
 */
const redactSsoClientSecret = (body: string): string | undefined => {
  let parsed: unknown
  try {
    parsed = JSON.parse(body)
  } catch {
    return undefined
  }

  const operations = Array.isArray(parsed) ? parsed : [parsed]

  for (const operation of operations) {
    const variables = (
      operation as {
        variables?: {
          settings?: Record<string, unknown>
          client?: { settings?: Record<string, unknown> }
        }
      }
    )?.variables

    const settings = variables?.client?.settings ?? variables?.settings
    if (!settings || typeof settings !== 'object') return undefined

    if (settings.ssoClientSecret) settings.ssoClientSecret = REDACTED
  }

  return JSON.stringify(parsed)
}

export const requestSanitizer = (request: IRequest): IRequest | null => {
  // logrocket doesnt need to see all our elastic telemetry
  if ((request?.url ?? '').includes('fh-elastic.apm')) return null

  if ((request?.url ?? '').includes('payarc')) return null

  // plaster over the auth header
  for (const key in request.headers)
    if (key.toLowerCase() === 'authorization')
      request.headers[key] = '00000000-0000-0000-0000-000000000000'

  // strip the credentials out of a processor validation without losing the operation
  if (request.body?.toLowerCase?.().includes('validateprocessorcredentials'))
    request.body = redactProcessorCredentials(request.body)

  // strip the write-only Okta client secret out of a client create/update. Match the quoted
  // variables key: the bare substring would also hit isSsoClientSecretSet in the client
  // fragment text and wrongly drop every client/user list query.
  if (request.body?.toLowerCase?.().includes('"ssoclientsecret"'))
    request.body = redactSsoClientSecret(request.body)

  // hide bodies with password
  if (request.body?.toLowerCase?.().includes('password'))
    request.body = undefined

  // OIDC login and re-auth proofs: the authorization code + PKCE verifier (loginOidc,
  // SsoReauthInput) and Google ID tokens are one-time credentials — hide the whole body.
  // The ssoReauth match also keeps the new PIN out of updateOwnUserPin captures.
  if (request.body?.toLowerCase?.().includes('codeverifier'))
    request.body = undefined

  if (request.body?.toLowerCase?.().includes('ssoreauth'))
    request.body = undefined

  if (request.body?.toLowerCase?.().includes('logingoogle'))
    request.body = undefined

  // hide bodies with ACH info
  if (request.body?.toLowerCase?.().includes('accountnumber'))
    request.body = undefined

  if (request.body?.toLowerCase?.().includes('routingnumber'))
    request.body = undefined

  // just in case, hide credit card (not used yet)
  if (request.body?.toLowerCase?.().includes('creditcard'))
    request.body = undefined

  // AgentAPI token exchange posts the Flowhub session id as subject_token.
  // Keep the URL/method for debugging, but never capture the body.
  const url = (request?.url ?? '').toLowerCase()
  const body = request.body?.toLowerCase?.() ?? ''
  if (url.includes('/oauth/token') || body.includes('subject_token')) {
    request.body = undefined
  }

  // Intercom Messenger identity JWT — anyone with the body can boot as that user.
  if (body.includes('intercomuserjwt')) {
    request.body = undefined
  }

  // Agent GraphQL can carry customer-adjacent preview payloads.
  if (url.includes('/agentapi/query')) {
    request.body = undefined
  }

  return request
}

export const responseSanitizer = (response: IResponse): IResponse | null => {
  if (response.body?.toLowerCase?.().includes('token'))
    response.body = undefined

  if (response.body?.toLowerCase?.().includes('intercomuserjwt'))
    response.body = undefined

  // hide bodies with ACH info
  if (response.body?.toLowerCase?.().includes('accountnumber'))
    response.body = undefined

  if (response.body?.toLowerCase?.().includes('routingnumber'))
    response.body = undefined

  // just in case, hide credit card (not used yet)
  if (response.body?.toLowerCase?.().includes('creditcard'))
    response.body = undefined

  // Agent GraphQL responses can include customerIds in tool/preview payloads.
  // LogRocket does not expose the matching request URL here, so redact when the
  // body looks like an agentapi GraphQL envelope.
  const responseBody = response.body?.toLowerCase?.() ?? ''
  if (
    responseBody.includes('sendmessage') ||
    responseBody.includes('ensureagentsession') ||
    responseBody.includes('agentprogress')
  ) {
    response.body = undefined
  }

  return response
}
